import moment from "moment";
import trim from "lodash/trim";
import i18n from "i18n";
import { isTypeAbsolute, isTypeRelativeWithEnd, isTypeKeyword } from "views/typeGuards/timeRange";
import { isValidDate, toDateObject } from "util/DateTime";
import debounceWithPromise from "views/logic/debounceWithPromise";
import { testNaturalDate } from "api/tools";
const invalidDateFormatError = () => i18n.t("common_views.time_range_validation.invalid_date_format", {
  ns: "search",
  format: "YYYY-MM-DD [HH:mm:ss[.SSS]]"
});
const rangeLimitError = () => i18n.t("common_views.time_range_validation.range_limit", { ns: "search" });
const dateLimitError = () => i18n.t("common_views.time_range_validation.date_limit", { ns: "search" });
const timeRangeError = () => i18n.t("common_views.time_range_validation.until_before_from", { ns: "search" });
const emptyError = () => i18n.t("common_views.time_range_validation.empty", { ns: "search" });
const exceedsDuration = (dateTime, limitDuration, formatTime) => {
  if (limitDuration === 0) {
    return false;
  }
  const durationLimit = formatTime(toDateObject(/* @__PURE__ */ new Date()).subtract(Number(limitDuration), "seconds"), "complete");
  return moment(dateTime).isBefore(durationLimit);
};
const validateAbsoluteTimeRange = (timeRange, limitDuration, formatTime) => {
  let errors = {};
  if (!isValidDate(timeRange.from)) {
    errors = { ...errors, from: invalidDateFormatError() };
  }
  if (!isValidDate(timeRange.to)) {
    errors = { ...errors, to: invalidDateFormatError() };
  }
  if (timeRange.from >= timeRange.to) {
    errors = { ...errors, to: timeRangeError() };
  }
  if (exceedsDuration(timeRange.from, limitDuration, formatTime)) {
    errors = { ...errors, from: dateLimitError() };
  }
  return errors;
};
const validateRelativeTimeRangeWithEnd = (timeRange, limitDuration) => {
  let errors = {};
  if (limitDuration > 0) {
    if (timeRange.from > limitDuration || !timeRange.from) {
      errors = { ...errors, from: rangeLimitError() };
    }
    if (timeRange.to > limitDuration) {
      errors = { ...errors, to: rangeLimitError() };
    }
  }
  if (timeRange.from === null) {
    errors = { ...errors, from: emptyError() };
  }
  if (timeRange.from && timeRange.to === null) {
    errors = { ...errors, to: emptyError() };
  }
  if (timeRange.from && timeRange.from <= timeRange.to) {
    errors = { ...errors, to: timeRangeError() };
  }
  return errors;
};
const debouncedTestNaturalDate = debounceWithPromise(testNaturalDate, 350);
const validateKeywordTimeRange = async (timeRange, limitDuration, formatTime, userTimezone, testKeyword) => {
  let errors = {};
  if (trim(timeRange.keyword) === "") {
    errors = { keyword: i18n.t("common_views.time_range_validation.keyword_empty", { ns: "search" }) };
    return errors;
  }
  if (testKeyword) {
    const actualTimeRange = await debouncedTestNaturalDate(timeRange.keyword, userTimezone).catch(() => void 0);
    if (!actualTimeRange) {
      errors = { keyword: i18n.t("common_views.time_range_validation.keyword_unparsable", { ns: "search" }) };
      return errors;
    }
    if (exceedsDuration(formatTime(actualTimeRange.from, "complete"), limitDuration, formatTime)) {
      errors = { keyword: rangeLimitError() };
      return errors;
    }
  }
  return errors;
};
const validateTimeRange = (timeRange, limitDuration, formatTime, userTimezone, testKeyword = true) => {
  if (isTypeKeyword(timeRange)) {
    return validateKeywordTimeRange(timeRange, limitDuration, formatTime, userTimezone, testKeyword);
  }
  if (isTypeRelativeWithEnd(timeRange)) {
    return validateRelativeTimeRangeWithEnd(timeRange, limitDuration);
  }
  if (isTypeAbsolute(timeRange)) {
    return validateAbsoluteTimeRange(timeRange, limitDuration, formatTime);
  }
  return {};
};
export default validateTimeRange;
